// This app-root error-boundary file intentionally co-locates the class boundary with its small
// fallback component; fast-refresh does not meaningfully apply to a top-level boundary.
/* eslint-disable react-refresh/only-export-components */
import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kidshome/ui';
import { ErrorState } from '@/shared/ui';
import { reportRouteError } from '@/shared/lib/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Recovery card shown by {@link ErrorBoundary}. Mirrors {@link RouteError}'s UI (shared kind
 * `ErrorState` + retry / go-home) — a function component so it can pull i18n, which is mounted by
 * `AppProviders` ABOVE this boundary. Reload re-runs the whole app (the most reliable recovery for a
 * transient failure, e.g. a stale lazy chunk); the home link is the way out when one route is broken.
 */
function ErrorBoundaryFallback(): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg p-6 text-text"
      role="alert"
    >
      <ErrorState />
      <div className="flex gap-2">
        <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
          {t('states.retry')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => (window.location.href = '/')}>
          {t('nav.home')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Last-resort React error boundary wrapping `<RouterProvider>` (FSD `app`). The RR7 `errorElement`
 * ({@link RouteError}) only catches throws INSIDE a matched route's tree; a throw OUTSIDE it — a
 * lazy-chunk rejection on a route with no `errorElement`, or a render error in `RouterProvider`
 * itself — would otherwise white-screen the whole app. This class boundary catches anything the route
 * boundaries miss and renders the same kind recovery card, so no throw degrades to a blank page.
 *
 * It mirrors {@link RouteError}'s UI rather than rendering it, because `RouteError` calls
 * `useRouteError()` (router context), which is unavailable for an error in `RouterProvider` itself.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown): void {
    // Forward to Sentry (M7 36a) — a no-op when Sentry is disabled (no VITE_SENTRY_DSN); PII is
    // scrubbed in beforeSend. Fire-and-forget so reporting never blocks the recovery UI.
    void reportRouteError(error);
  }

  override render(): ReactNode {
    return this.state.hasError ? <ErrorBoundaryFallback /> : this.props.children;
  }
}
